import { ApplicationInsights } from '@microsoft/applicationinsights-web';

let ai: ApplicationInsights | null = null;

export interface GsTelemetryConfig {
  /** Connection string Application Insights. Vide = NO-OP silencieux (dev local). */
  connectionString: string;
  /** Identifiant de l'app pour filtrage : 'hub' | 'admin' | 'inventaire' | 'fit' | ... */
  appKey: string;
  /** Object ID Azure AD — JAMAIS l'email (PII). */
  userId?: string;
  /** SHA du commit courant (injecté au build par CI via VITE_GS_GIT_SHA). */
  gitSha?: string;
}

/**
 * Initialise Application Insights Browser SDK. À appeler une seule fois au boot
 * de l'app (dans `main.tsx`, avant `ReactDOM.createRoot`).
 *
 * Idempotent : les ré-appels sont no-op.
 * Tolérant : si `connectionString` est vide (dev local), ne fait rien — pas de throw.
 */
export function initTelemetry(config: GsTelemetryConfig): void {
  if (ai) return;
  if (!config.connectionString) return;

  try {
    ai = new ApplicationInsights({
      config: {
        connectionString: config.connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: false,
        autoTrackPageVisitTime: true,
        disableFetchTracking: false,
        disableExceptionTracking: false,
      },
    });
    ai.loadAppInsights();

    ai.addTelemetryInitializer((envelope) => {
      if (envelope.tags) {
        envelope.tags['ai.cloud.role'] = config.appKey;
      }
      const data = (envelope.data ?? {}) as Record<string, unknown>;
      if (config.gitSha) data.gitSha = config.gitSha;
      data.appKey = config.appKey;
      envelope.data = data;
    });

    if (config.userId) {
      ai.setAuthenticatedUserContext(config.userId, undefined, true);
    }

    ai.trackPageView();
  } catch (err) {
    ai = null;
    if (typeof console !== 'undefined') {
      console.warn('[gs/telemetry] init failed, telemetry disabled:', err);
    }
  }
}

/** Track une exception JS avec props additionnelles (path, correlationId, status, …). */
export function trackError(err: unknown, extraProps?: Record<string, unknown>): void {
  if (!ai) return;
  const error = err instanceof Error ? err : new Error(String(err));
  try {
    ai.trackException({ exception: error, properties: extraProps });
  } catch {
    /* swallow */
  }
}

/** Track un événement custom (feature usage, clic bouton, etc.). */
export function trackEvent(name: string, props?: Record<string, unknown>): void {
  if (!ai) return;
  try {
    ai.trackEvent({ name, properties: props });
  } catch {
    /* swallow */
  }
}

/** Associe l'utilisateur courant à la session télémétrie (Azure AD object ID uniquement). */
export function setTelemetryUser(userId: string): void {
  if (!ai) return;
  try {
    ai.setAuthenticatedUserContext(userId, undefined, true);
  } catch {
    /* swallow */
  }
}

/** Dissocie l'utilisateur (au logout). */
export function clearTelemetryUser(): void {
  if (!ai) return;
  try {
    ai.clearAuthenticatedUserContext();
  } catch {
    /* swallow */
  }
}

/** Indique si le SDK a été initialisé et est actif. */
export function isTelemetryEnabled(): boolean {
  return ai !== null;
}
